import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import FilterModal from '../components/FilterModal';
import Pagination from '../components/Pagination';
import { projectsAPI, showConfirmModal } from '../services/api';
import { Search, Edit, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { usePersistedState } from '../hooks/usePersistedState';
import { useBoardAutoRefresh } from '../hooks/useBoardAutoRefresh';
import BoardControls from '../components/BoardControls';
import ScrollSyncWrapper from '../components/ScrollSyncWrapper';
import ColumnToggle from '../components/ColumnToggle';
import { useHiddenColumns } from '../hooks/useHiddenColumns';
import { useColumnWidths } from '../hooks/useColumnWidths';
import { useColumnOrder } from '../hooks/useColumnOrder';
import InvoiceSentDateCell from '../components/InvoiceSentDateCell';
import CopyButton from '../components/CopyButton';
import MapsLink from '../components/MapsLink';
import { formatNoteAuthor } from '../utils/formatNoteAuthor';
import FilteredPill from '../components/FilteredPill';
import { useFranchiseColors } from '../hooks/useFranchiseColors';
import { namesMatch } from '../utils/nameMatch';
import { formatNoteTimestamp, noteTimestamp } from '../utils/noteDate';
import { readResponseJSON } from '../utils/fetchSafe';
import { dedupeProjectsById } from '../utils/dedupeProjects';

// Columns the user can toggle on/off from the Columns dropdown.
// Project # and the Actions column are intentionally not togglable.
const TOGGLABLE_COLUMNS = [
  { key: 'pnd_date', label: 'FNOL Date' },
  { key: 'customer', label: 'Customer' },
  { key: 'job_location', label: 'Job Location' },
  { key: 'pre_tax_total_value', label: 'Subtotal' },
  { key: 'profit_margin', label: 'Profit Margin' },
  { key: 'estimator', label: 'Estimator' },
  { key: 'office', label: 'Office' },
  { key: 'production', label: 'Production' },
  { key: 'job_type', label: 'Job Type' },
  { key: 'job_status', label: 'Job Status' },
  { key: 'job_payment', label: 'Job Payment' },
  { key: 'signed_auth', label: 'Signed Auth' },
  { key: 'upload_reqr', label: 'Upload Required' },
  { key: 'last_note_action', label: 'Last Note/Action' },
  { key: 'invoice', label: 'Invoice' },
  { key: 'invoice_sent_date', label: 'Invoice Sent Date' },
  { key: 'aging_days', label: 'Aging (Days)' },
];

const Projects = () => {
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  const cols = useHiddenColumns('open', user?.email);
  useColumnWidths('open', user?.email);
  const colOrder = useColumnOrder('open', user?.email, [
    'pnd_date', 'customer', 'job_location', 'pre_tax_total_value', 'profit_margin',
    'estimator', 'office', 'production', 'job_type', 'job_status',
    'job_payment', 'signed_auth', 'upload_reqr', 'last_note_action', 'invoice',
    'invoice_sent_date', 'aging_days',
  ]);
  const { colorFor } = useFranchiseColors();
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState({});
  const [searchTerm, setSearchTerm] = usePersistedState('restorepm:projects:search', '');
  const [entriesPerPage, setEntriesPerPage] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [noteModal, setNoteModal] = useState({ open: false, projectId: null });
  const [noteText, setNoteText] = useState('');
  const [noteUrgent, setNoteUrgent] = useState(false);
  const [sortColumn, setSortColumn] = usePersistedState('restorepm:projects:sortColumn', 'estimator');
  const [sortDirection, setSortDirection] = usePersistedState('restorepm:projects:sortDirection', 'asc');
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filters, setFilters] = usePersistedState('restorepm:projects:filters', {
    job_status: '', job_type: '', payment_type: '', paid_in_full: '',
    estimator: '', office_contact: '', production_mgr: '', marketing_rep: '', project_lead: '',
    fnol_from: '', fnol_to: '', subtotal_from: '', subtotal_to: ''
  });
  const [employees, setEmployees] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);

  const filterFields = [
    { type: 'date_range', label: 'FNOL Date', fromKey: 'fnol_from', toKey: 'fnol_to' },
    { key: 'job_status', label: 'Job Status' },
    { key: 'job_type', label: 'Job Type' },
    { type: 'number_range', label: 'Subtotal', fromKey: 'subtotal_from', toKey: 'subtotal_to' },
    { key: 'estimator', label: 'Estimator' },
    { key: 'production_mgr', label: 'Production Mgr' },
    { key: 'office_contact', label: 'Office Contact' },
    { key: 'marketing_rep', label: 'Marketing Rep' },
    { key: 'project_lead', label: 'Project Lead' },
    { key: 'payment_type', label: 'Payment Type' },
    { key: 'paid_in_full', label: 'Paid in Full' },
  ];

  useEffect(() => {
    fetchProjects();
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API_URL}/api/employees?active=true`).then(readResponseJSON).then(d => setEmployees(d.employees || [])).catch(() => {});
    fetch(`${API_URL}/api/project-types`).then(readResponseJSON).then(d => setProjectTypes(d.project_types || [])).catch(() => {});
  }, []);

  const fetchProjects = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const data = await projectsAPI.getAll('open');
      setProjects(dedupeProjectsById(data.projects || []));
      setStats(data.stats || {});
    } catch (error) {
      if (!silent) toast.error('Failed to fetch projects');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Auto-refetch when the user returns to the tab or refocuses the window
  // so the board can never show stale data after editing a project.
  useBoardAutoRefresh(fetchProjects);

  const handleRowClick = (projectId) => {
    navigate(`/projects/${projectId}`);
  };

  const handleEditClick = (e, projectId) => {
    e.stopPropagation(); // Prevent row click when clicking edit button
    navigate(`/projects/${projectId}`);
  };

  const handleDelete = async (e, projectId) => {
    e.stopPropagation();
    e.preventDefault();
    if (!(await showConfirmModal('Are you sure you want to delete this project?'))) return;
    try {
      await projectsAPI.delete(projectId);
      toast.success('Project deleted successfully');
      fetchProjects();
    } catch (error) {
      if (error.response?.status === 401) return;
      const detail = error.response?.data?.detail || 'Failed to delete project';
      toast.error(detail);
    }
  };

  const handleNoteClick = (e, projectId) => {
    e.stopPropagation();
    setNoteModal({ open: true, projectId });
    setNoteText('');
    setNoteUrgent(false);
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      await projectsAPI.addNote(noteModal.projectId, { text: noteText.trim(), urgent: noteUrgent });
      toast.success('Note added');
      setNoteModal({ open: false, projectId: null });
      fetchProjects();
    } catch { toast.error('Failed to add note'); }
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === 'water') return 'bg-[#00BFFF]';
    if (statusLower === 'check-in') return 'bg-[#00BFFF]';
    if (statusLower === 'in progress') return 'bg-[#DC143C]';
    if (statusLower === 'mold') return 'bg-[#32CD32]';
    return 'bg-gray-400';
  };

  const getTerritoryColor = (territory) => colorFor(territory);

  const getJobTypeCellClass = (jobType) => {
    const t = (jobType || '').toLowerCase();
    if (t === 'reconstruction') return 'bg-[#FFF3CD] text-gray-800';
    if (t === 'pack out' || t === 'packout') return 'bg-[#9B7CB8] text-white';
    if (t === 'bad debt') return 'bg-[#E86B9A] text-white';
    if (t === 'storage') return 'bg-[#C8A2D8] text-gray-800';
    if (t === 'water') return 'bg-[#87CEEB] text-gray-800';
    if (t === 'mold') return 'bg-[#90EE90] text-gray-800';
    if (t === 'fire') return 'bg-[#F08080] text-white';
    if (t === 'cleaning') return 'bg-[#40E0D0] text-gray-800';
    if (t === 'duct cleaning') return 'bg-[#FFD700] text-gray-800';
    if (t === 'biohazard') return 'bg-[#DC143C] text-white';
    if (t === 'check-in') return 'bg-[#87CEEB] text-gray-800';
    if (t === 'in progress') return 'bg-[#DC143C] text-white';
    if (t === 'claim') return 'bg-[#FF69B4] text-white';
    if (t === 'supplemental water') return 'bg-[#5FBDCC] text-white';
    return 'bg-gray-100 text-gray-800';
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortValue = (val, column) => {
    if (['invoice', 'aging_days', 'pre_tax_total_value'].includes(column)) {
      const num = parseFloat(String(val).replace(/[$,]/g, ''));
      return isNaN(num) ? 0 : num;
    }
    if (['pnd_date', 'start_date', 'finish_date'].includes(column)) {
      if (!val || val === 'n/a') return 0;
      const d = new Date(val);
      return isNaN(d.getTime()) ? 0 : d.getTime();
    }
    return String(val || '').toLowerCase();
  };

  const renderSortIcon = (column) => (
    <span className={`ml-1 text-[10px] ${sortColumn === column ? 'text-gray-700' : 'text-gray-300'}`}>
      {sortColumn === column ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}
    </span>
  );

  const filteredProjects = projects.filter((project) => {
    // Apply modal filters
    const f = filters;
    if (f.job_status) {
      const selected = f.job_status.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
      if (selected.length && !selected.includes((project.job_status || '').toLowerCase())) return false;
    }
    if (f.job_type && project.job_type !== f.job_type) return false;
    if (f.payment_type && project.job_payment !== f.payment_type) return false;
    if (f.paid_in_full) {
      const isPaid = project.paid_in_full || project.paid === 'Yes';
      if (f.paid_in_full === 'Yes' && !isPaid) return false;
      if (f.paid_in_full === 'No' && isPaid) return false;
    }
    if (f.estimator && !namesMatch(f.estimator, project.estimator)) return false;
    if (f.office_contact && !namesMatch(f.office_contact, project.office)) return false;
    if (f.production_mgr && !namesMatch(f.production_mgr, project.production)) return false;
    if (f.marketing_rep && !namesMatch(f.marketing_rep, project.marketing_rep)) return false;
    if (f.project_lead && !namesMatch(f.project_lead, project.project_lead)) return false;
    if (f.fnol_from || f.fnol_to) {
      const pndDate = project.pnd_date || project.fnol_date || '';
      if (f.fnol_from && pndDate < f.fnol_from) return false;
      if (f.fnol_to && pndDate > f.fnol_to) return false;
    }
    if (f.subtotal_from || f.subtotal_to) {
      const val = parseFloat(String(project.pre_tax_total_value || '0').replace(/[$,]/g, '')) || 0;
      if (f.subtotal_from) { const from = parseFloat(String(f.subtotal_from).replace(/[$,]/g, '')) || 0; if (val < from) return false; }
      if (f.subtotal_to) { const to = parseFloat(String(f.subtotal_to).replace(/[$,]/g, '')) || 0; if (val > to) return false; }
    }
    // Apply search term — every whitespace-separated token must appear somewhere
    // in the concatenated searchable fields. This lets "Joseph Smith" match
    // a customer stored as "Smith, Joseph".
    if (!searchTerm.trim()) return true;
    const haystack = [
      project.project_id, project.customer, project.job_location,
      project.address, project.estimator, project.office,
      project.production, project.job_type, project.job_status,
      project.job_payment, project.last_note_action, project.invoice,
      project.pnd_date, project.phone, project.email
    ].map(f => String(f || '').toLowerCase()).join(' ');
    const tokens = searchTerm.toLowerCase().split(/\s+/).filter(Boolean);
    return tokens.every(tok => haystack.includes(tok));
  });

  const getLastNoteDate = (project) => {
    const notes = project.notes_list;
    if (notes?.length > 0) return Math.max(...notes.map(noteTimestamp));
    return project.last_note_action || '';
  };

  // Compute profit margin (%) for a project. Uses the canonical backend
  // financials block when present (matches the project detail page formula),
  // otherwise falls back to (revenue - expenses_list_sum) / revenue.
  const getProfitMargin = (project) => {
    if (project.financials) return project.financials.profit_margin;
    const revenue = parseFloat(String(project.pre_tax_total_value || '0').replace(/[$,]/g, '')) || 0;
    if (revenue <= 0) return null;
    const expenses = (project.expenses_list || []).reduce(
      (s, e) => s + (parseFloat(String(e.amount || '0').replace(/[$,]/g, '')) || 0),
      0
    );
    return ((revenue - expenses) / revenue) * 100;
  };

  const sortedProjects = [...filteredProjects].sort((a, b) => {
    if (!sortColumn) return 0;
    if (sortColumn === 'profit_margin') {
      const aVal = getProfitMargin(a);
      const bVal = getProfitMargin(b);
      const dir = sortDirection === 'asc' ? 1 : -1;
      // Treat null (no revenue) as -Infinity so they always sort to the bottom in desc.
      const aNum = aVal === null ? -Infinity : aVal;
      const bNum = bVal === null ? -Infinity : bVal;
      if (aNum < bNum) return -1 * dir;
      if (aNum > bNum) return 1 * dir;
      return 0;
    }
    const aRaw = sortColumn === 'last_note_action' ? getLastNoteDate(a) : a[sortColumn];
    const bRaw = sortColumn === 'last_note_action' ? getLastNoteDate(b) : b[sortColumn];
    // Push blank/empty values to the bottom regardless of direction so
    // sorting alphabetically by Estimator doesn't surface unassigned projects first.
    const aBlank = aRaw === null || aRaw === undefined || String(aRaw).trim() === '';
    const bBlank = bRaw === null || bRaw === undefined || String(bRaw).trim() === '';
    if (aBlank && !bBlank) return 1;
    if (!aBlank && bBlank) return -1;
    if (aBlank && bBlank) return 0;
    const aVal = getSortValue(aRaw, sortColumn);
    const bVal = getSortValue(bRaw, sortColumn);
    const dir = sortDirection === 'asc' ? 1 : -1;
    if (aVal < bVal) return -1 * dir;
    if (aVal > bVal) return 1 * dir;
    return 0;
  });

  const displayedProjects = sortedProjects.slice((currentPage - 1) * entriesPerPage, currentPage * entriesPerPage);

  // Reset to first page whenever filters/search/page-size change so we never land on an empty page.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, entriesPerPage, filters, sortColumn, sortDirection]);

  return (
    <Layout>
      <div className="p-4 lg:p-8">
        {/* Header + Stats Cards (top-right) */}
        <div className="flex items-start justify-between gap-6 mb-8">
          <div>
            <h2 className="text-2xl lg:text-3xl text-[#5B8DB8] font-light mb-1">Projects</h2>
            <h1 className="text-3xl lg:text-4xl text-[#5B8DB8] font-light">All Available Projects</h1>
          </div>
          <div className="flex flex-wrap justify-end gap-3 lg:gap-6">
            {(() => {
              const isFiltered = Object.values(filters).some((v) => v !== '') || Boolean((searchTerm || '').trim());
              // Stat cards reflect the *currently filtered* set so changes
              // in the Edit Filters modal cascade up here. Without this,
              // filtering by Estimator (etc.) would leave the totals stuck
              // on the unfiltered backend numbers — which is confusing.
              //
              // "In Progress" is now strictly job_status === "In Progress".
              // On-Hold projects are excluded because On-Hold is used as a
              // long-term storage bucket for future jobs and skews the
              // active-work counts.
              const statusKey = (p) => (p.job_status || p.status || '').toLowerCase().replace(/[\s_-]/g, '');
              const isInProgress = (p) => statusKey(p) === 'inprogress';
              const isCompleted = (p) => statusKey(p) === 'completed';
              const sumValue = (arr) => arr.reduce((acc, p) => {
                const v = parseFloat(String(p.pre_tax_total_value || '0').replace(/[$,]/g, '')) || 0;
                return acc + v;
              }, 0);

              const inProgressF = filteredProjects.filter(isInProgress);
              const inProgressAll = projects.filter(isInProgress);
              const completedF = filteredProjects.filter(isCompleted);
              const completedAll = projects.filter(isCompleted);
              const totalF = filteredProjects;
              const totalAll = projects;

              const Card = ({ label, filteredArr, allArr, onClick, testId }) => {
                const fCount = filteredArr.length;
                const fValue = sumValue(filteredArr);
                const aCount = allArr.length;
                const aValue = sumValue(allArr);
                const fmt = (n) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
                return (
                  <button
                    type="button"
                    onClick={onClick}
                    data-testid={testId}
                    title="Click to filter the board to this group"
                    className="bg-white rounded shadow px-4 lg:px-6 py-4 border-l-4 border-gray-400 text-left cursor-pointer transition hover:shadow-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#FFA94D]"
                  >
                    <div className="text-xs text-gray-500 uppercase mb-1">{label} <FilteredPill active={isFiltered} /></div>
                    <div className="text-2xl lg:text-3xl font-light text-gray-700">
                      {fCount}
                      {isFiltered && (
                        <span className="text-xs text-gray-400 ml-1" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}-of`}>of {aCount}</span>
                      )}
                      {' '}
                      <span className="text-sm text-gray-500">
                        {fmt(fValue)}
                        {isFiltered && (
                          <span className="text-xs text-gray-400 ml-1">of {fmt(aValue)}</span>
                        )}
                      </span>
                    </div>
                  </button>
                );
              };

              // Quick-filter shortcuts: clicking a card narrows the board
              // to that status (or clears all filters for "Total"). The
              // FilterModal stays in sync because both write to the same
              // `filters` state.
              const setStatusFilter = (val) => setFilters((prev) => ({ ...prev, job_status: val }));
              const clearAll = () => setFilters({
                job_status: '', job_type: '', payment_type: '', paid_in_full: '',
                estimator: '', office_contact: '', production_mgr: '', marketing_rep: '', project_lead: '',
                fnol_from: '', fnol_to: '', subtotal_from: '', subtotal_to: ''
              });

              return (
                <>
                  <Card
                    label="In Progress"
                    filteredArr={inProgressF}
                    allArr={inProgressAll}
                    onClick={() => setStatusFilter('In Progress')}
                    testId="stat-card-in-progress"
                  />
                  <Card
                    label="Completed Projects"
                    filteredArr={completedF}
                    allArr={completedAll}
                    onClick={() => setStatusFilter('Completed')}
                    testId="stat-card-completed"
                  />
                  <Card
                    label="Total"
                    filteredArr={totalF}
                    allArr={totalAll}
                    onClick={clearAll}
                    testId="stat-card-total"
                  />
                </>
              );
            })()}
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white rounded shadow p-4 lg:p-6 mb-6">
          <BoardControls
            filterButtons={
              <>
                <button onClick={() => setShowFilterModal(true)} className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-6 py-2 rounded transition-colors" data-testid="edit-filters-btn">
                  Edit Filters
                </button>
                {Object.values(filters).some(v => v !== '') && (
                  <button
                    onClick={() => setFilters({ job_status: '', job_type: '', payment_type: '', paid_in_full: '', estimator: '', office_contact: '', production_mgr: '', marketing_rep: '', project_lead: '', fnol_from: '', fnol_to: '' })}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-5 py-2 rounded text-sm font-medium transition-colors"
                    data-testid="clear-filters-btn"
                  >
                    Clear Filters
                  </button>
                )}
              </>
            }
            pinnedView={{
              board: 'open',
              currentState: { filters, searchTerm, sortColumn, sortDirection },
              onApply: (s) => {
                if (s.filters) setFilters(s.filters);
                if (typeof s.searchTerm === 'string') setSearchTerm(s.searchTerm);
                if (s.sortColumn !== undefined) setSortColumn(s.sortColumn);
                if (s.sortDirection) setSortDirection(s.sortDirection);
              },
            }}
            columnToggle={
              <ColumnToggle
                columns={TOGGLABLE_COLUMNS}
                hidden={cols.hidden}
                onToggle={cols.toggle}
                onReset={cols.reset}
                onResetOrder={colOrder.reset}
                testIdSuffix="open"
              />
            }
            entriesPerPage={entriesPerPage}
            setEntriesPerPage={setEntriesPerPage}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />

          {/* Table */}
          {cols.styleTag}
          <ScrollSyncWrapper>
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading projects...</div>
            ) : projects.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No projects found</div>
            ) : (
              (() => {
              // Per-column metadata — same data-driven pattern as the
              // AR board, so JSX renders both <th> and <td> in the
              // user's chosen order. See AccountsReceivable.jsx for
              // the rationale on why we can't rely on post-render DOM
              // manipulation.
              // eslint-disable-next-line react/no-unstable-nested-components
              const colDefs = {
                pnd_date: {
                  th: () => <th className="col-pnd_date text-left p-2 text-gray-600 font-normal cursor-pointer select-none" onClick={() => handleSort('pnd_date')} data-testid="sort-pnd-date" key="pnd_date"><span className="inline-flex items-center">FNOL Date{renderSortIcon('pnd_date')}</span></th>,
                  td: (project) => <td className="col-pnd_date p-2 text-gray-700 whitespace-nowrap" key="pnd_date">{project.pnd_date}</td>,
                },
                customer: {
                  th: () => <th className="col-customer text-left p-2 text-gray-600 font-normal cursor-pointer select-none" onClick={() => handleSort('customer')} data-testid="sort-customer" key="customer"><span className="inline-flex items-center">Customer{renderSortIcon('customer')}</span></th>,
                  td: (project) => (
                    <td className="col-customer p-2" key="customer">
                      <div className="whitespace-nowrap">
                        {project.customer_id ? (
                          <Link to={`/customers/${project.customer_id}`} className="text-[#5B8DB8] hover:text-[#3a6a91] hover:underline" data-testid={`customer-link-${project.project_id}`}>
                            {project.customer}
                          </Link>
                        ) : (
                          <span className="text-gray-700">{project.customer}</span>
                        )}
                      </div>
                      {project.phone && <div className="text-gray-400 text-xs">{project.phone}</div>}
                      {project.email && <div className="text-gray-400 text-xs">{project.email}</div>}
                    </td>
                  ),
                },
                job_location: {
                  th: () => <th className="col-job_location text-left p-2 text-gray-600 font-normal cursor-pointer select-none" onClick={() => handleSort('job_location')} data-testid="sort-job-location" key="job_location"><span className="inline-flex items-center">Job Location{renderSortIcon('job_location')}</span></th>,
                  td: (project) => {
                    const loc = project.address || project.job_location;
                    return (
                      <td className="col-job_location p-2" key="job_location">
                        <MapsLink address={loc} testid={`job-location-link-${project.project_id}`} />
                      </td>
                    );
                  },
                },
                pre_tax_total_value: {
                  th: () => <th className="col-pre_tax_total_value text-left p-2 text-gray-600 font-normal cursor-pointer select-none" onClick={() => handleSort('pre_tax_total_value')} data-testid="sort-subtotal" key="pre_tax_total_value"><span className="inline-flex items-center">Subtotal{renderSortIcon('pre_tax_total_value')}</span></th>,
                  td: (project) => <td className="col-pre_tax_total_value p-2 text-gray-700 whitespace-nowrap" key="pre_tax_total_value">{project.pre_tax_total_value ? `$${parseFloat(project.pre_tax_total_value).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : ''}</td>,
                },
                profit_margin: {
                  th: () => <th className="col-profit_margin text-left p-2 text-gray-600 font-normal cursor-pointer select-none" onClick={() => handleSort('profit_margin')} data-testid="sort-profit-margin" key="profit_margin"><span className="inline-flex items-center">Profit Margin{renderSortIcon('profit_margin')}</span></th>,
                  td: (project) => <td className="col-profit_margin p-2 text-gray-700 whitespace-nowrap" key="profit_margin">{(() => { const m = getProfitMargin(project); return m === null ? '' : `${m.toFixed(2)}%`; })()}</td>,
                },
                estimator: {
                  th: () => <th className="col-estimator text-left p-2 text-gray-600 font-normal cursor-pointer select-none" onClick={() => handleSort('estimator')} data-testid="sort-estimator" key="estimator"><span className="inline-flex items-center">Estimator{renderSortIcon('estimator')}</span></th>,
                  td: (project) => <td className={`col-estimator p-2 ${project.estimator_action ? 'text-red-600 font-semibold' : 'text-gray-700'}`} key="estimator">{project.estimator}</td>,
                },
                office: {
                  th: () => <th className="col-office text-left p-2 text-gray-600 font-normal cursor-pointer select-none" onClick={() => handleSort('office')} data-testid="sort-office" key="office"><span className="inline-flex items-center">Office{renderSortIcon('office')}</span></th>,
                  td: (project) => <td className={`col-office p-2 ${project.office_action ? 'text-red-600 font-semibold' : 'text-gray-700'}`} key="office">{project.office}</td>,
                },
                production: {
                  th: () => <th className="col-production text-left p-2 text-gray-600 font-normal cursor-pointer select-none" onClick={() => handleSort('production')} data-testid="sort-production" key="production"><span className="inline-flex items-center">Production{renderSortIcon('production')}</span></th>,
                  td: (project) => <td className="col-production p-2 text-gray-700" key="production">{project.production}</td>,
                },
                job_type: {
                  th: () => <th className="col-job_type text-left p-2 text-gray-600 font-normal cursor-pointer select-none" onClick={() => handleSort('job_type')} data-testid="sort-job-type" key="job_type"><span className="inline-flex items-center">Job Type{renderSortIcon('job_type')}</span></th>,
                  td: (project) => <td className={`col-job_type p-2 text-xs ${getJobTypeCellClass(project.job_type)}`} key="job_type">{project.job_type}</td>,
                },
                job_status: {
                  th: () => <th className="col-job_status text-left p-2 text-gray-600 font-normal cursor-pointer select-none" onClick={() => handleSort('job_status')} data-testid="sort-job-status" key="job_status"><span className="inline-flex items-center">Job Status{renderSortIcon('job_status')}</span></th>,
                  td: (project) => <td className="col-job_status p-2 text-gray-700" key="job_status">{project.job_status}</td>,
                },
                job_payment: {
                  th: () => <th className="col-job_payment text-left p-2 text-gray-600 font-normal cursor-pointer select-none" onClick={() => handleSort('job_payment')} data-testid="sort-job-payment" key="job_payment"><span className="inline-flex items-center">Job Payment{renderSortIcon('job_payment')}</span></th>,
                  td: (project) => <td className="col-job_payment p-2 text-gray-700" key="job_payment">{project.job_payment}</td>,
                },
                signed_auth: {
                  th: () => <th className="col-signed_auth text-left p-2 text-gray-600 font-normal cursor-pointer select-none" onClick={() => handleSort('signed_auth')} data-testid="sort-signed-auth" key="signed_auth"><span className="inline-flex items-center">Signed Auth{renderSortIcon('signed_auth')}</span></th>,
                  td: (project) => (
                    <td className="col-signed_auth p-2" key="signed_auth">
                      <span className={project.signed_auth === 'Yes' ? 'text-green-600' : 'text-red-600'}>
                        {project.signed_auth || 'No'}
                      </span>
                    </td>
                  ),
                },
                upload_reqr: {
                  th: () => <th className="col-upload_reqr text-left p-2 text-gray-600 font-normal cursor-pointer select-none" onClick={() => handleSort('upload_reqr')} data-testid="sort-upload-reqr" key="upload_reqr"><span className="inline-flex items-center">Upload Required{renderSortIcon('upload_reqr')}</span></th>,
                  td: (project) => (
                    <td className="col-upload_reqr p-2" key="upload_reqr">
                      <span className={project.upload_reqr === 'Yes' ? 'text-red-600 font-semibold' : 'text-gray-700'}>
                        {project.upload_reqr || 'No'}
                      </span>
                    </td>
                  ),
                },
                last_note_action: {
                  th: () => <th className="col-last_note_action text-left p-2 text-gray-600 font-normal cursor-pointer select-none min-w-[280px]" onClick={() => handleSort('last_note_action')} data-testid="sort-last-note" key="last_note_action"><span className="inline-flex items-center">Last Note/Action{renderSortIcon('last_note_action')}</span></th>,
                  td: (project) => (
                    <td className="col-last_note_action p-2" key="last_note_action">
                      {(() => {
                        const notesList = project.notes_list || [];
                        if (notesList.length === 0) {
                          return <div className="text-xs text-gray-700">{project.last_note_action}</div>;
                        }
                        const noteTs = (n) => noteTimestamp(n);
                        const recent = [...notesList].sort((a, b) => noteTs(b) - noteTs(a)).slice(0, 2);
                        return recent.map((note, nIdx) => {
                          const isUrgent = note.urgent;
                          const author = formatNoteAuthor(note.created_by);
                          return (
                            <div key={nIdx} className={`${nIdx > 0 ? 'mt-2 pt-2 border-t border-gray-100' : ''}`}>
                              <div className={`text-xs whitespace-nowrap ${isUrgent ? 'text-red-600' : 'text-gray-700'}`}>
                                {formatNoteTimestamp(note)}
                                {author && <span className="text-gray-400 ml-2">· {author}</span>}
                              </div>
                              <div title={note.text} className={`text-xs max-w-[280px] line-clamp-2 cursor-help ${isUrgent ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>{note.text}</div>
                            </div>
                          );
                        });
                      })()}
                    </td>
                  ),
                },
                invoice: {
                  th: () => <th className="col-invoice text-left p-2 text-gray-600 font-normal cursor-pointer select-none" onClick={() => handleSort('invoice')} data-testid="sort-invoice" key="invoice"><span className="inline-flex items-center">Invoice{renderSortIcon('invoice')}</span></th>,
                  td: (project) => <td className="col-invoice p-2 text-gray-700" key="invoice">{(() => { const v = project.invoice_number || project.invoice; return v && v !== '0' ? v : ''; })()}</td>,
                },
                invoice_sent_date: {
                  th: () => <th className="col-invoice_sent_date text-left p-2 text-gray-600 font-normal cursor-pointer select-none whitespace-nowrap" onClick={() => handleSort('invoice_sent_date')} data-testid="sort-invoice-sent" key="invoice_sent_date"><span className="inline-flex items-center">Invoice Sent Date{renderSortIcon('invoice_sent_date')}</span></th>,
                  td: (project) => (
                    <td className="col-invoice_sent_date p-2 text-gray-700 whitespace-nowrap" data-testid={`invoice-sent-${project.project_id}`} key="invoice_sent_date">
                      <InvoiceSentDateCell project={project} />
                    </td>
                  ),
                },
                aging_days: {
                  th: () => <th className="col-aging_days text-left p-2 text-gray-600 font-normal cursor-pointer select-none" onClick={() => handleSort('aging_days')} data-testid="sort-aging-days" key="aging_days"><span className="inline-flex items-center">Aging (Days){renderSortIcon('aging_days')}</span></th>,
                  td: (project) => <td className="col-aging_days p-2 text-gray-700" key="aging_days">{project.aging_days}</td>,
                },
              };
              const ordered = colOrder.order.filter((k) => colDefs[k]);
              Object.keys(colDefs).forEach((k) => { if (!ordered.includes(k)) ordered.push(k); });
              return (
            <table className="w-full text-sm border-collapse" data-board-table="open">
              <thead>
                <tr className="border-b-2">
                  <th className="col-project_id text-left p-2 text-gray-600 font-normal cursor-pointer select-none" onClick={() => handleSort('project_id')} data-testid="sort-project-id"><span className="inline-flex items-center">Project #{renderSortIcon('project_id')}</span></th>
                  {ordered.map((key) => colDefs[key].th())}
                  <th className="text-left p-2 text-gray-600 font-normal sticky right-0 bg-white shadow-[-6px_0_8px_-2px_rgba(0,0,0,0.15)] z-10 border-l border-gray-200"></th>
                </tr>
              </thead>
              <tbody>
                {displayedProjects.map((project, index) => (
                  <tr
                    key={project._id || project.project_id}
                    data-testid={`project-row-${project.project_id || project.id}`}
                    className={`border-b transition-colors align-top ${project.upload_reqr === 'Yes' ? 'bg-red-100 hover:bg-red-200' : `hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}`}
                  >
                    <td
                      className="p-2 pl-3"
                      style={{ borderLeft: `8px solid ${getTerritoryColor(project.territory)}` }}
                    >
                      <div className="flex flex-col items-start">
                        <span className="text-gray-700">{project.project_id}</span>
                        <CopyButton value={project.project_id} testid={`copy-project-id-${project.project_id}`} />
                      </div>
                    </td>
                    {ordered.map((key) => colDefs[key].td(project))}
                    <td className={`p-2 sticky right-0 shadow-[-6px_0_8px_-2px_rgba(0,0,0,0.15)] z-10 min-w-[120px] border-l border-gray-200 ${project.upload_reqr === 'Yes' ? 'bg-red-100' : (index % 2 === 0 ? 'bg-white' : 'bg-gray-50')}`}>
                      <div className="flex items-center gap-1">
                        {hasPermission('can_update') && (
                        <a
                          href={`/projects/${project._id || project.project_id}`}
                          className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white rounded p-1.5 inline-flex items-center justify-center"
                          onClick={(e) => { e.stopPropagation(); }}
                          data-testid={`edit-btn-${project.project_id}`}
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </a>
                        )}
                        <button
                          className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white rounded p-1.5"
                          onClick={(e) => handleNoteClick(e, project._id || project.project_id)}
                          data-testid={`note-btn-${project.project_id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        {hasPermission('can_delete') && (
                        <button
                          className="bg-[#DC3545] hover:bg-[#C82333] text-white rounded p-1.5"
                          onClick={(e) => handleDelete(e, project._id || project.project_id)}
                          data-testid={`delete-btn-${project.project_id}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
              );
            })()
          )}
          </ScrollSyncWrapper>
          <Pagination
            total={sortedProjects.length}
            entriesPerPage={entriesPerPage}
            currentPage={currentPage}
            onChange={setCurrentPage}
            testIdPrefix="projects-pagination"
          />
        </div>
      </div>
      {/* Note Modal */}
      {noteModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-16 z-50" onMouseDown={(e) => { if (e.target === e.currentTarget) setNoteModal({ open: false, projectId: null }); }}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()} data-testid="note-modal">
            <textarea
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              placeholder="Enter a project note"
              className="w-full border border-gray-300 rounded p-3 text-sm resize-y min-h-[160px] mb-4"
              data-testid="note-textarea"
              autoFocus
            />
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={noteUrgent}
                  onChange={e => setNoteUrgent(e.target.checked)}
                  className="w-4 h-4"
                  data-testid="note-urgent-checkbox"
                />
                Mark as Urgent
              </label>
              <div className="flex gap-2">
                <button
                  onClick={handleAddNote}
                  className="bg-[#DC3545] hover:bg-[#C82333] text-white px-5 py-2 rounded text-sm font-medium transition-colors"
                  data-testid="add-note-btn"
                >
                  Add Note
                </button>
                <button
                  onClick={() => setNoteModal({ open: false, projectId: null })}
                  className="bg-[#6C7B8A] hover:bg-[#5A6878] text-white px-5 py-2 rounded text-sm font-medium transition-colors"
                  data-testid="close-note-btn"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <FilterModal
        open={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        filters={filters}
        setFilters={setFilters}
        onUpdate={() => setShowFilterModal(false)}
        fields={filterFields}
        employees={employees}
        projectTypes={projectTypes}
      />
    </Layout>
  );
};

export default Projects;
